import { useEffect, useState } from 'react';
import { complianceAPI, downloadAuthFile, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ShieldCheck, FileDown, Plus, Pencil, Trash2, Loader2, ScrollText, Bot } from 'lucide-react';
import { toast } from 'sonner';

const FRAMEWORKS = [
    { key: 'rgpd', label: 'RGPD', desc: 'Données personnelles des employés et contacts' },
    { key: 'securite', label: 'Sécurité applicative', desc: 'Accès, sessions, cloisonnement' },
    { key: 'ai_act', label: 'AI Act', desc: 'Usages IA et suivi Reguardian' },
];
const STATUS = {
    conforme: { label: 'Conforme', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    a_corriger: { label: 'À corriger', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    a_surveiller: { label: 'À surveiller', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    non_applicable: { label: 'Non applicable', cls: 'bg-slate-50 text-slate-400 border-slate-200' },
};
const ORDER = { a_corriger: 0, a_surveiller: 1, conforme: 2, non_applicable: 3 };

function ChecklistItem({ item, onUpdate }) {
    const [note, setNote] = useState(item.note || '');
    const [editing, setEditing] = useState(false);
    const st = STATUS[item.status] || STATUS.conforme;
    return (
        <div className="border border-slate-100 rounded-lg p-3 bg-white" data-testid={`compliance-item-${item.key}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{item.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{editing ? null : item.note}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{item.reference} · vérifié le {item.checked_at}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    <Select value={item.status} onValueChange={v => onUpdate(item.id, { status: v })}>
                        <SelectTrigger className={`h-7 text-xs w-36 border ${st.cls}`} data-testid={`compliance-status-${item.key}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <button className="text-[10px] text-teal-700 underline" onClick={() => setEditing(e => !e)} data-testid={`compliance-edit-note-${item.key}`}>
                        {editing ? 'annuler' : 'modifier la note'}
                    </button>
                </div>
            </div>
            {editing && (
                <div className="mt-2 flex gap-2">
                    <Textarea value={note} onChange={e => setNote(e.target.value)} className="text-xs min-h-[50px]" />
                    <Button size="sm" className="h-8 bg-teal-700 hover:bg-teal-800" onClick={() => { onUpdate(item.id, { note }); setEditing(false); }}>OK</Button>
                </div>
            )}
        </div>
    );
}

function RegistryDialog({ entry, onSave, onClose }) {
    const [form, setForm] = useState(entry || { name: '', data: '', purpose: '', access: '', retention: '' });
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="registry-dialog">
                <DialogHeader><DialogTitle className="text-base">{entry ? 'Modifier le traitement' : 'Nouveau traitement'}</DialogTitle></DialogHeader>
                <div className="space-y-2">
                    {[['name', 'Traitement'], ['data', 'Données concernées'], ['purpose', 'Finalité'], ['access', 'Qui y accède'], ['retention', 'Durée de conservation']].map(([k, l]) => (
                        <div key={k}>
                            <label className="text-xs font-medium text-slate-600">{l}</label>
                            <Input value={form[k]} onChange={set(k)} className="h-8 text-sm" data-testid={`registry-field-${k}`} />
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={onClose}>Annuler</Button>
                    <Button size="sm" className="bg-teal-700 hover:bg-teal-800" disabled={!form.name.trim()} onClick={() => onSave(form)} data-testid="registry-save-btn">Enregistrer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function CompliancePage() {
    const [data, setData] = useState(null);
    const [regDialog, setRegDialog] = useState(null); // null | 'new' | entry
    const [pdfBusy, setPdfBusy] = useState(false);

    const load = () => complianceAPI.overview().then(r => setData(r.data)).catch(e => toast.error(e?.response?.data?.detail || 'Chargement impossible'));
    useEffect(() => { load(); }, []);

    const updateItem = async (id, payload) => {
        try { await complianceAPI.updateItem(id, payload); load(); } catch { toast.error('Mise à jour impossible'); }
    };
    const saveRegistry = async (form) => {
        try {
            if (regDialog === 'new') await complianceAPI.addRegistry(form);
            else await complianceAPI.updateRegistry(regDialog.id, form);
            setRegDialog(null); load();
        } catch { toast.error('Enregistrement impossible'); }
    };

    if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-teal-700" /></div>;

    const items = data.items || [];
    const counters = (fw) => {
        const list = items.filter(i => i.framework === fw);
        return { ok: list.filter(i => i.status === 'conforme').length, todo: list.filter(i => i.status === 'a_corriger').length, watch: list.filter(i => i.status === 'a_surveiller').length };
    };

    return (
        <div className="space-y-6" data-testid="compliance-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-teal-700" />Conformité</h1>
                    <p className="text-sm text-slate-500">RGPD · Sécurité applicative · AI Act — audit initial du 01/06/2026, suivi en continu.</p>
                </div>
                <Button variant="outline" size="sm" disabled={pdfBusy} data-testid="compliance-pdf-btn" onClick={async () => {
                    setPdfBusy(true);
                    await downloadAuthFile(`${API_BASE}/compliance/report/pdf`, 'rapport_conformite.pdf');
                    setPdfBusy(false);
                }}>
                    {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}Rapport PDF
                </Button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
                {FRAMEWORKS.map(fw => {
                    const c = counters(fw.key);
                    return (
                        <div key={fw.key} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`compliance-card-${fw.key}`}>
                            <p className="font-semibold text-slate-800 flex items-center gap-1.5">{fw.key === 'ai_act' && <Bot className="w-4 h-4 text-indigo-600" />}{fw.label}</p>
                            <p className="text-xs text-slate-400">{fw.desc}</p>
                            <div className="flex gap-2 mt-2 text-xs">
                                <Badge variant="outline" className={STATUS.conforme.cls}>{c.ok} conformes</Badge>
                                {c.todo > 0 && <Badge variant="outline" className={STATUS.a_corriger.cls}>{c.todo} à corriger</Badge>}
                                {c.watch > 0 && <Badge variant="outline" className={STATUS.a_surveiller.cls}>{c.watch} à surveiller</Badge>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {FRAMEWORKS.map(fw => (
                <div key={fw.key}>
                    <h2 className="text-sm font-semibold text-slate-600 uppercase mb-2">{fw.label}</h2>
                    <div className="space-y-2">
                        {items.filter(i => i.framework === fw.key).sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9))
                            .map(i => <ChecklistItem key={i.id} item={i} onUpdate={updateItem} />)}
                    </div>
                </div>
            ))}

            <div>
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-slate-600 uppercase">Registre des traitements</h2>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setRegDialog('new')} data-testid="registry-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter</Button>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead><tr className="text-left text-slate-500 border-b border-slate-100">
                            {['Traitement', 'Données', 'Finalité', 'Accès', 'Conservation', ''].map(h => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
                        </tr></thead>
                        <tbody data-testid="registry-table">
                            {(data.registry || []).map(r => (
                                <tr key={r.id} className="border-b border-slate-50 align-top">
                                    <td className="px-3 py-2 font-medium text-slate-700">{r.name}</td>
                                    <td className="px-3 py-2 text-slate-500">{r.data}</td>
                                    <td className="px-3 py-2 text-slate-500">{r.purpose}</td>
                                    <td className="px-3 py-2 text-slate-500">{r.access}</td>
                                    <td className="px-3 py-2 text-slate-500">{r.retention}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <button className="text-slate-400 hover:text-teal-700 mr-2" onClick={() => setRegDialog(r)} data-testid={`registry-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                        <button className="text-slate-400 hover:text-red-600" data-testid={`registry-delete-${r.id}`} onClick={async () => {
                                            if (!window.confirm(`Retirer « ${r.name} » du registre ?`)) return;
                                            try { await complianceAPI.deleteRegistry(r.id); load(); } catch { toast.error('Suppression impossible'); }
                                        }}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div>
                <h2 className="text-sm font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1.5"><ScrollText className="w-4 h-4" />Journal de conformité</h2>
                <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1.5" data-testid="compliance-log">
                    {(data.log || []).map(l => (
                        <p key={l.id} className="text-xs text-slate-600">
                            <span className="text-slate-400">{new Date(l.at).toLocaleDateString('fr-FR')} · {l.by}</span> — {l.text}
                        </p>
                    ))}
                </div>
            </div>

            <p className="text-[11px] text-slate-400">
                Suivi AI Act : procédure Reguardian (reguardian.eu) — toute future fonction IA est analysée avant mise en ligne, les corrections sont enregistrées comme preuve tierce. Aide à la décision, pas un avis juridique.
            </p>

            {regDialog && <RegistryDialog entry={regDialog === 'new' ? null : regDialog} onSave={saveRegistry} onClose={() => setRegDialog(null)} />}
        </div>
    );
}
